import type { FC, ReactNode } from 'react'
import type { ServicePageProps } from '@/lib/page-data'

import { CircleDollarSign } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import PageHero from '@/components/sections/page-hero'
import StoryHero from '@/components/sections/story-hero'
import QuickAnswer from '@/components/sections/quick-answer'
import ExteriorOnlyPanel from '@/components/sections/exterior-only-panel'
import OptionCards from '@/components/sections/option-cards'
import ComparisonTable from '@/components/sections/comparison-table'
import DetailSections from '@/components/sections/detail-sections'
import JobSteps from '@/components/sections/job-steps'
import JobPhotoGrid from '@/components/sections/job-photo-grid'
import ReviewGrid from '@/components/sections/review-grid'
import FaqList from '@/components/sections/faq-list'
import PostCards from '@/components/sections/post-cards'
import AreaLinks from '@/components/sections/area-links'
import CtaBand from '@/components/sections/cta-band'
import ParallaxPhoto from '@/components/site/parallax-photo'
import { breadcrumbSchema, faqSchema, serviceSchema } from '@/lib/schema'

import { getImage } from '@/content/images'
import { STORY_HEROES } from '@/content/story-heroes'

const childrenTitle = (kind: string, name: string): string => {
    if (kind === 'hub') return `${name}: choose what you need`
    if (name.toLowerCase().includes('door')) return "Exterior door types we install"

    return `More about ${name.toLowerCase()}`
}

const ServicePage: FC<ServicePageProps> = ({ page, crumbs, children, related, photos, reviews, posts }): ReactNode => {
    const story = STORY_HEROES[page.path]
    const image = getImage(page.image)
    const shareImage = story ? getImage(story.image) : image
    const windowStyles = children.filter(link => link.href.startsWith('/window-options/'))
    const otherChildren = children.filter(link => !link.href.startsWith('/window-options/'))
    const faq = faqSchema(page.faqs)
    const jsonLd = [
        serviceSchema({ name: page.name, description: page.quickAnswer, path: page.path, image: shareImage.src }),
        breadcrumbSchema(crumbs, page.path),
        ...(faq ? [faq] : [])
    ]

    const optionBlueprint = page.category === 'siding'
        ? 'siding'
        : page.category === 'roofing'
            ? 'roof'
            : page.category === 'washing'
                ? 'house'
                : page.kind === 'door-type' || page.path.includes('door') ? 'door' : 'window'

    return (
        <Layout>
            <Seo
                title={page.seoTitle}
                description={page.metaDescription}
                path={page.path}
                ogImage={shareImage.src}
                jsonLd={jsonLd}
            />

            {story
                ? (
                    <StoryHero
                        crumbs={crumbs}
                        titleLines={story.titleLines}
                        lead={page.lead}
                        image={getImage(story.image)}
                        imagePosition={story.imagePosition}
                        note={story.note}
                        statement={story.statement}
                        exteriorOnly={page.exteriorOnly}
                    />
                )
                : (
                    <PageHero
                        crumbs={crumbs}
                        title={page.h1}
                        lead={page.lead}
                        image={page.diagram ? null : image}
                        diagram={page.diagram}
                        exteriorOnly={page.exteriorOnly}
                    />
                )}

            <QuickAnswer answer={page.quickAnswer} facts={page.facts} />

            {page.diagram && (
                <section aria-label='Photo' className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                    <ParallaxPhoto image={image} className='aspect-[16/9] max-h-[34rem]' sizes='(min-width: 1280px) 1200px, 100vw' />
                </section>
            )}

            {page.exteriorOnly && (page.kind === 'hub' || page.kind === 'service') && <ExteriorOnlyPanel />}

            {otherChildren.length > 0 && (
                <OptionCards
                    id='choices'
                    title={childrenTitle(page.kind, page.name)}
                    links={otherChildren}
                    tone='tinted'
                    blueprint={optionBlueprint}
                />
            )}

            {windowStyles.length > 0 && (
                <OptionCards
                    id='window-styles'
                    title="Exterior window styles"
                    intro="Each style opens differently. The drawings show how, so you can picture it on your home."
                    links={windowStyles}
                    blueprint='window'
                />
            )}

            {page.comparison && <ComparisonTable comparison={page.comparison} />}

            <DetailSections sections={page.sections} />

            {page.priceDrivers && page.priceDrivers.length > 0 && (
                <section aria-labelledby='price-heading' className='mx-auto max-w-7xl px-4 pb-14 sm:px-6 lg:px-8'>
                    <div className='rounded-3xl bg-navy p-8 text-white sm:p-10' data-reveal>
                        <h2 id='price-heading' className='flex items-center gap-3 text-3xl font-bold text-white'>
                            <CircleDollarSign className='size-8 text-gold' aria-hidden='true' />
                            What affects the price
                        </h2>

                        <p className='mt-3 max-w-3xl text-lg text-white/90'>
                            We price after measuring, line by line, with the materials named. These are the things that move the number.
                        </p>

                        <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                            {page.priceDrivers.map(driver => (
                                <li key={driver} className='rounded-2xl bg-white/10 px-5 py-4 text-lg'>
                                    {driver}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            <JobSteps />

            <JobPhotoGrid
                photos={photos}
                title="From our recent jobs"
                intro="Real ACM job photos. Every photo marked “ACM job photo” is from one of our own jobs."
            />

            <ReviewGrid reviews={reviews} tone='plain' />

            <FaqList faqs={page.faqs} />

            {related.length > 0 && (
                <OptionCards id='related' title="Related services" links={related} tone='tinted' />
            )}

            <PostCards posts={posts} title="Guides on this topic" />

            <AreaLinks serviceName={page.name} />

            <CtaBand />
        </Layout>
    )
}

export default ServicePage
